import { HeaderMenu } from "@/interfaces/HeaderMenu";
import * as Consts from "@/scripts/Consts";
import * as NavHeaderConsts from "@/scripts/NavHeaderConsts";

export type AccountMenuState = {
  title: string;
  route?: string;
  icon: string;
  content: boolean;
};

/**
 * Menus always present in the bar. Children (articles, classes, projects)
 * are attached later and must stay on these same objects.
 */
export function createBaseMenus(): HeaderMenu[] {
  return [
    new HeaderMenu({
      title: "Accueil",
      icon: "home",
      route: "/",
      id: NavHeaderConsts.HOME_MENU,
    }),
    new HeaderMenu({
      title: "Articles",
      icon: "book-variant",
      route: "/articles",
      id: NavHeaderConsts.ARTICLES_MENU,
    }),
    new HeaderMenu({
      title: "Classes",
      icon: "school",
      route: "/classes",
      id: NavHeaderConsts.CLASSES_MENU,
    }),
    new HeaderMenu({
      title: "Projets d'école",
      icon: "google-classroom",
      route: "/projets-ecole",
      id: NavHeaderConsts.PROJECTS_MENU,
    }),
    new HeaderMenu({
      title: "Archives",
      icon: "archive",
      route: "/archives",
      id: NavHeaderConsts.ARCHIVES_MENU,
    }),
    new HeaderMenu({
      title: "Contact",
      icon: "card-account-mail",
      route: "/contact",
      id: NavHeaderConsts.CONTACT_MENU,
    }),
  ];
}

export function getDisplayedMenus(
  baseMenus: HeaderMenu[],
  loggedIn: boolean,
  userStatus: number,
): HeaderMenu[] {
  const visibleBase = loggedIn
    ? baseMenus
    : baseMenus.filter((menu) =>
        NavHeaderConsts.PUBLIC_MENU_IDS.includes(menu.id),
      );

  if (!loggedIn) return visibleBase;
  return [...visibleBase, ...getRoleMenus(userStatus)];
}

export function getAccountMenu(
  loggedIn: boolean,
  variant: "desktop" | "mobile",
): AccountMenuState {
  if (!loggedIn) {
    return {
      title: "Connexion",
      route: "/connexion",
      icon: variant === "mobile" ? "login" : "login-variant",
      content: false,
    };
  }

  if (variant === "mobile") {
    return {
      title: "Déconnexion",
      icon: "logout",
      content: true,
    };
  }

  return {
    title: "Mon compte",
    icon: "account",
    content: true,
  };
}

export function getUserStatusLabel(userStatus: number): string {
  switch (userStatus) {
    case Consts.user_status_classroom:
      return "Classe";
    case Consts.user_status_parents:
      return "Parent";
    case Consts.user_status_teacher:
      return "Enseignant";
    case Consts.user_status_admin:
      return "Administrateur";
    default:
      return "";
  }
}

function getRoleMenus(userStatus: number): HeaderMenu[] {
  switch (userStatus) {
    case Consts.user_status_teacher:
      return [createTeacherAdminMenu()];
    case Consts.user_status_admin:
      return [createAdminMenu()];
    case Consts.user_status_classroom:
      return [createClassroomMenu()];
    default:
      return [];
  }
}

function createTeacherAdminMenu(): HeaderMenu {
  return new HeaderMenu({
    title: "Administration",
    icon: "shield-edit",
    id: NavHeaderConsts.ADMIN_MENU,
    children: [
      new HeaderMenu({
        title: "Créer article",
        route: "/gestion/creer-article",
        id: NavHeaderConsts.ADMIN_CREATE_ARTICLE_MENU,
      }),
      new HeaderMenu({
        title: "Ma classe",
        route: "/gestion/ma-classe",
        id: NavHeaderConsts.ADMIN_MY_CLASSROOM_MENU,
      }),
      new HeaderMenu({
        title: "Projets d'école",
        route: "/gestion/projets-ecole",
        id: NavHeaderConsts.ADMIN_SCHOOL_PROJECTS_MENU,
      }),
      new HeaderMenu({
        title: "Gestion des classes",
        route: "/gestion/classes",
        id: NavHeaderConsts.ADMIN_CLASSROOMS_MENU,
      }),
      new HeaderMenu({
        title: "Gestion d'accueil",
        route: "/gestion/accueil",
        id: NavHeaderConsts.ADMIN_HOME_MENU,
      }),
      new HeaderMenu({
        title: "Statistiques",
        route: "/gestion/statistiques",
        id: NavHeaderConsts.ADMIN_STAT_MENU,
      }),
    ],
  });
}

function createAdminMenu(): HeaderMenu {
  return new HeaderMenu({
    title: "Administration",
    icon: "shield-edit",
    id: NavHeaderConsts.ADMIN_MENU,
    children: [
      new HeaderMenu({
        title: "Projets d'école",
        route: "/gestion/projets-ecole",
        id: NavHeaderConsts.ADMIN_SCHOOL_PROJECTS_MENU,
      }),
      new HeaderMenu({
        title: "Gestion des classes",
        route: "/gestion/classes",
        id: NavHeaderConsts.ADMIN_CLASSROOMS_MENU,
      }),
      new HeaderMenu({
        title: "Gestion d'accueil",
        route: "/gestion/accueil",
        id: NavHeaderConsts.ADMIN_HOME_MENU,
      }),
      new HeaderMenu({
        title: "Gestion des enseignants",
        route: "/gestion/enseignants",
        id: NavHeaderConsts.ADMIN_TEACHERS_MENU,
      }),
      new HeaderMenu({
        title: "Gestion des parents",
        route: "/gestion/parents",
        id: NavHeaderConsts.ADMIN_PARENTS_MENU,
      }),
      new HeaderMenu({
        title: "Statistiques",
        route: "/gestion/statistiques",
        id: NavHeaderConsts.ADMIN_STAT_MENU,
      }),
    ],
  });
}

function createClassroomMenu(): HeaderMenu {
  return new HeaderMenu({
    title: "Création",
    icon: "file-document-edit",
    id: NavHeaderConsts.CLASS_MENU,
    children: [
      new HeaderMenu({
        title: "Créer article",
        route: "/classe/creer-article",
        id: NavHeaderConsts.CLASS_CREATE_ARTICLE_MENU,
      }),
      new HeaderMenu({
        title: "Mes articles",
        route: "/classe/mes-articles",
        id: NavHeaderConsts.CLASS_MY_ARTICLES_MENU,
      }),
    ],
  });
}
